import React, { useState, useEffect, useMemo } from 'react';
import {
  Input,
  Button,
  DatePicker,
  message,
  Tag,
  Popconfirm,
  Typography,
  Empty,
  Spin,
  Modal
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  CalendarFilled,
  UnorderedListOutlined,
  AppstoreOutlined,
  CloudUploadOutlined,
  FileExcelOutlined,
  LinkOutlined,
  SortAscendingOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import {
  getAllOffDates,
  addMultipleOffDates,
  removeOffDate,
  getEmployees
} from '../../api/supabase';
import { colors, spacing, radius, typography, shadows, transitions } from '../../styles/monochromeTheme';
import { isGoogleAuthenticated, getGoogleAuthUrl } from '../../api/googleDrive';
import { exportOffDaysToSheet, createOffDaysSheet } from '../../api/googleSheetsExport';
import * as XLSX from 'xlsx';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Styles
const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xxxxl,
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xl,
    marginBottom: spacing.xxxxl,
    paddingBottom: spacing.xxl,
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  headerIcon: {
    width: '56px',
    height: '56px',
    background: colors.bgDark,
    borderRadius: radius.xxl,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
  },
  headerTitle: {
    margin: 0,
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    letterSpacing: typography.letterSpacing.tight,
  },
  headerSubtitle: {
    margin: 0,
    fontSize: typography.fontSize.md,
    color: colors.textTertiary,
    marginTop: spacing.xs,
  },
  searchSection: {
    marginBottom: spacing.xxxl,
  },
  searchInput: {
    height: '56px',
    borderRadius: radius.xl,
    border: `2px solid ${colors.borderLight}`,
    fontSize: typography.fontSize.lg,
    paddingLeft: spacing.xl,
  },
  employeeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: spacing.xl,
  },
  employeeCard: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xxl,
    transition: transitions.normal,
  },
  employeeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
  },
  employeeInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
  },
  employeeAvatar: {
    width: '48px',
    height: '48px',
    background: colors.bgDark,
    borderRadius: radius.xl,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.textInverse,
    fontWeight: typography.fontWeight.bold,
    fontSize: typography.fontSize.lg,
  },
  employeeName: {
    margin: 0,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  employeeRole: {
    margin: 0,
    fontSize: typography.fontSize.sm,
    color: colors.textTertiary,
  },
  statsRow: {
    display: 'flex',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  statBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
    padding: `${spacing.xs} ${spacing.md}`,
    borderRadius: radius.md,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
  },
  lastDateSection: {
    background: colors.bgTertiary,
    borderRadius: radius.xl,
    padding: spacing.lg,
    marginBottom: spacing.lg,
  },
  lastDateLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.wide,
    marginBottom: spacing.sm,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  lastDateValue: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textPrimary,
  },
  lastDateAgo: {
    fontSize: typography.fontSize.sm,
    color: colors.textTertiary,
    marginLeft: spacing.sm,
  },
  quickAddSection: {
    display: 'flex',
    gap: spacing.sm,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  quickAddBtn: {
    height: '40px',
    borderRadius: radius.lg,
    fontWeight: typography.fontWeight.semibold,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  primaryBtn: {
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
  },
  secondaryBtn: {
    background: colors.bgSecondary,
    border: `1px solid ${colors.borderMedium}`,
    color: colors.textSecondary,
  },
  offBtn: {
    background: '#f6ffed',
    border: '2px solid #52c41a',
    color: '#389e0d',
  },
  astreinteBtn: {
    background: '#fff7e6',
    border: '2px solid #fa8c16',
    color: '#d46b08',
  },
  baseBtn: {
    background: '#e6fffb',
    border: '2px solid #13c2c2',
    color: '#08979c',
  },
  baseAmBtn: {
    background: '#e6f7ff',
    border: '2px solid #1890ff',
    color: '#096dd9',
  },
  basePmBtn: {
    background: '#f0f5ff',
    border: '2px solid #597ef7',
    color: '#2f54eb',
  },
  historySection: {
    marginTop: spacing.lg,
    paddingTop: spacing.lg,
    borderTop: `1px solid ${colors.borderSubtle}`,
  },
  historyLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.wide,
    marginBottom: spacing.sm,
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  historyItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${spacing.sm} ${spacing.md}`,
    background: colors.bgOverlay,
    borderRadius: radius.md,
    fontSize: typography.fontSize.sm,
  },
  emptyState: {
    textAlign: 'center',
    padding: spacing.xxxxl,
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
};

const OffDays = () => {
  const [employees, setEmployees] = useState([]);
  const [offDates, setOffDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(dayjs());

  // View mode: 'manage' for employee cards, 'overview' for timeline view
  const [viewMode, setViewMode] = useState('manage');
  const [overviewFilter, setOverviewFilter] = useState('all'); // 'all', 'OFF', 'ASTREINTE', 'BASE'

  // Multi-date selection state
  const [multiDateModalVisible, setMultiDateModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('OFF');
  const [selectedDates, setSelectedDates] = useState([]);

  // Bulk employee selection modal state
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [bulkSelectedEmployees, setBulkSelectedEmployees] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('OFF');
  const [bulkSearchQuery, setBulkSearchQuery] = useState('');
  const [bulkDate, setBulkDate] = useState(dayjs());

  // Google Sheets export state
  const [exporting, setExporting] = useState(false);
  const [sheetUrl, setSheetUrl] = useState(localStorage.getItem('offdays_sheet_url') || '');

  // Sort mode: 'none', 'off_old', 'off_recent', 'astreinte_old', 'astreinte_recent', 'base_old', 'base_recent'
  const [sortMode, setSortMode] = useState('none');

  // Show/hide recommendations section
  const [showRecommendations, setShowRecommendations] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [employeesResult, offDatesData] = await Promise.all([
        getEmployees(),
        getAllOffDates()
      ]);
      setEmployees(employeesResult.data || []);
      setOffDates(offDatesData.data || []);
    } catch (error) {
      message.error('Erreur lors du chargement des données');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Sheets export
  const handleExportToSheets = async () => {
    // Check if authenticated with Google
    if (!isGoogleAuthenticated()) {
      // Redirect to Google OAuth
      const authUrl = getGoogleAuthUrl();
      window.location.href = authUrl;
      return;
    }

    setExporting(true);
    try {
      // Reload fresh data from database before export
      message.loading('Chargement des données...', 0);
      const [employeesResult, offDatesData] = await Promise.all([
        getEmployees(),
        getAllOffDates()
      ]);
      const freshEmployees = employeesResult.data || [];
      const freshOffDates = offDatesData.data || [];

      // Update local state with fresh data
      setEmployees(freshEmployees);
      setOffDates(freshOffDates);
      message.destroy();

      // Check if we have a sheet ID configured
      const sheetId = process.env.REACT_APP_GOOGLE_OFFDAYS_SHEET_ID;

      let result;
      if (!sheetId) {
        // Create a new sheet
        message.loading('Création de la Google Sheet...', 0);
        const newSheet = await createOffDaysSheet();
        message.destroy();

        // Now export to the new sheet
        message.loading('Export des données...', 0);
        result = await exportOffDaysToSheet(freshOffDates, freshEmployees, newSheet.spreadsheetId);

        message.destroy();
        message.success('Google Sheet créée et données exportées !');

        // Save the URL for quick access
        setSheetUrl(newSheet.spreadsheetUrl);
        localStorage.setItem('offdays_sheet_url', newSheet.spreadsheetUrl);

        // Open the sheet in a new tab
        window.open(newSheet.spreadsheetUrl, '_blank');

        // Show info about configuring the sheet ID
        Modal.info({
          title: 'Google Sheet créée',
          content: (
            <div>
              <p>Une nouvelle Google Sheet a été créée avec succès !</p>
              <p style={{ marginTop: '12px' }}>
                Pour que l'export se fasse toujours dans le même fichier, ajoutez cet ID dans votre fichier .env :
              </p>
              <code style={{
                display: 'block',
                padding: '8px',
                background: '#f5f5f5',
                borderRadius: '4px',
                marginTop: '8px',
                wordBreak: 'break-all'
              }}>
                REACT_APP_GOOGLE_OFFDAYS_SHEET_ID={newSheet.spreadsheetId}
              </code>
            </div>
          ),
          width: 500
        });
      } else {
        // Export to existing sheet
        message.loading('Export vers Google Sheets...', 0);
        result = await exportOffDaysToSheet(freshOffDates, freshEmployees);
        message.destroy();

        setSheetUrl(result.sheetUrl);
        localStorage.setItem('offdays_sheet_url', result.sheetUrl);

        message.success(`${result.rowsWritten} lignes exportées vers Google Sheets !`);

        // Open the sheet
        window.open(result.sheetUrl, '_blank');
      }
    } catch (error) {
      message.destroy();
      console.error('Export error:', error);
      message.error(`Erreur lors de l'export: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Download as Excel (.xlsx)
  const handleDownloadExcel = async () => {
    try {
      // Reload fresh data from database
      message.loading('Chargement des données...', 0);
      const [employeesResult, offDatesData] = await Promise.all([
        getEmployees(),
        getAllOffDates()
      ]);
      const freshEmployees = employeesResult.data || [];
      const freshOffDates = offDatesData.data || [];
      message.destroy();

      // Create employee lookup
      const employeeMap = {};
      freshEmployees.forEach(emp => {
        const firstName = emp.first_name || '';
        const lastName = emp.name || '';
        employeeMap[emp.id] = `${firstName} ${lastName}`.trim() || 'Unknown';
      });

      // Sort by date descending
      const sortedData = [...freshOffDates].sort((a, b) =>
        new Date(b.off_date) - new Date(a.off_date)
      );

      // Prepare data for Excel
      const excelData = sortedData.map(entry => {
        const employee = freshEmployees.find(e => e.id === entry.employee_id);
        return {
          'Date': entry.off_date,
          'Employé': employeeMap[entry.employee_id] || 'Unknown',
          'Catégorie': entry.category,
          'Rôle': employee?.role || 'N/A'
        };
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'OFF & Astreintes');

      // Auto-size columns
      const colWidths = [
        { wch: 12 },  // Date
        { wch: 25 },  // Employé
        { wch: 15 },  // Catégorie
        { wch: 20 },  // Rôle
      ];
      worksheet['!cols'] = colWidths;

      // Generate and download file
      const today = dayjs().format('YYYY-MM-DD');
      XLSX.writeFile(workbook, `OFF_Astreintes_${today}.xlsx`);

      message.success(`${sortedData.length} entrées téléchargées !`);
    } catch (error) {
      message.destroy();
      console.error('Download error:', error);
      message.error(`Erreur lors du téléchargement: ${error.message}`);
    }
  };

  // Group off dates by employee
  const offDatesByEmployee = useMemo(() => {
    const grouped = {};
    offDates.forEach(od => {
      const empId = od.employee_id;
      if (!grouped[empId]) {
        grouped[empId] = [];
      }
      grouped[empId].push(od);
    });
    // Sort each employee's dates by date descending
    Object.keys(grouped).forEach(empId => {
      grouped[empId].sort((a, b) => dayjs(b.off_date).unix() - dayjs(a.off_date).unix());
    });
    return grouped;
  }, [offDates]);

  // Get employee stats
  const getEmployeeStats = (employeeId) => {
    const dates = offDatesByEmployee[employeeId] || [];
    const offCount = dates.filter(d => d.category === 'OFF').length;
    const astreinteCount = dates.filter(d => d.category === 'ASTREINTE').length;
    const baseAmCount = dates.filter(d => d.category === 'BASE_AM').length;
    const basePmCount = dates.filter(d => d.category === 'BASE_PM').length;
    const baseCount = dates.filter(d => d.category === 'BASE').length;
    const lastOff = dates.find(d => d.category === 'OFF');
    const lastAstreinte = dates.find(d => d.category === 'ASTREINTE');
    const lastBase = dates.find(d => d.category === 'BASE' || d.category === 'BASE_AM' || d.category === 'BASE_PM');
    return { offCount, astreinteCount, baseAmCount, basePmCount, baseCount, lastOff, lastAstreinte, lastBase, recentDates: dates.slice(0, 5) };
  };

  // Calculate recommendations - employees who most need OFF or ASTREINTE
  const recommendations = useMemo(() => {
    const now = dayjs();

    // Only consider ACTIVE employees for recommendations
    const activeEmployees = employees.filter(emp => emp.status === 'active');

    // Calculate days since last OFF/ASTREINTE for each employee
    const employeesWithStats = activeEmployees.map(emp => {
      const stats = getEmployeeStats(emp.id);

      // Days since last OFF (null if never had one)
      let daysSinceOff = null;
      if (stats.lastOff?.off_date) {
        daysSinceOff = now.diff(dayjs(stats.lastOff.off_date), 'day');
      }

      // Days since last ASTREINTE (null if never had one)
      let daysSinceAstreinte = null;
      if (stats.lastAstreinte?.off_date) {
        daysSinceAstreinte = now.diff(dayjs(stats.lastAstreinte.off_date), 'day');
      }

      return {
        employee: emp,
        stats,
        daysSinceOff,
        daysSinceAstreinte,
        hasNeverHadOff: daysSinceOff === null,
        hasNeverHadAstreinte: daysSinceAstreinte === null
      };
    });

    // Sort for OFF recommendations: never had OFF first, then by oldest OFF
    const needOff = [...employeesWithStats]
      .sort((a, b) => {
        // Both never had OFF
        if (a.hasNeverHadOff && b.hasNeverHadOff) return 0;
        // One never had OFF
        if (a.hasNeverHadOff) return -1;
        if (b.hasNeverHadOff) return 1;
        // Both have had OFF - sort by days (most days first)
        return b.daysSinceOff - a.daysSinceOff;
      })
      .slice(0, 5);

    // Sort for ASTREINTE recommendations: never had ASTREINTE first, then by oldest ASTREINTE
    const needAstreinte = [...employeesWithStats]
      .sort((a, b) => {
        // Both never had ASTREINTE
        if (a.hasNeverHadAstreinte && b.hasNeverHadAstreinte) return 0;
        // One never had ASTREINTE
        if (a.hasNeverHadAstreinte) return -1;
        if (b.hasNeverHadAstreinte) return 1;
        // Both have had ASTREINTE - sort by days (most days first)
        return b.daysSinceAstreinte - a.daysSinceAstreinte;
      })
      .slice(0, 5);

    return { needOff, needAstreinte };
  }, [employees, offDatesByEmployee]);

  // Filter and sort employees
  const filteredEmployees = useMemo(() => {
    // Only show ACTIVE employees
    let result = employees.filter(emp => emp.status === 'active');

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(emp =>
        emp.name?.toLowerCase().includes(query) ||
        emp.first_name?.toLowerCase().includes(query) ||
        emp.role?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    if (sortMode !== 'none') {
      result = [...result].sort((a, b) => {
        const statsA = getEmployeeStats(a.id);
        const statsB = getEmployeeStats(b.id);

        let dateA, dateB;
        let isRecentFirst = sortMode.endsWith('_recent');

        if (sortMode.startsWith('off')) {
          dateA = statsA.lastOff?.off_date;
          dateB = statsB.lastOff?.off_date;
        } else if (sortMode.startsWith('astreinte')) {
          dateA = statsA.lastAstreinte?.off_date;
          dateB = statsB.lastAstreinte?.off_date;
        } else if (sortMode.startsWith('base')) {
          dateA = statsA.lastBase?.off_date;
          dateB = statsB.lastBase?.off_date;
        }

        // Handle null dates
        if (!dateA && !dateB) return 0;
        if (!dateA) return isRecentFirst ? 1 : -1; // No date = needs one (goes first if oldest)
        if (!dateB) return isRecentFirst ? -1 : 1;

        // Sort order depends on mode
        if (isRecentFirst) {
          // Most recent first
          return new Date(dateB) - new Date(dateA);
        } else {
          // Oldest first (most distant from today = needs one soon)
          return new Date(dateA) - new Date(dateB);
        }
      });
    }

    return result;
  }, [employees, searchQuery, sortMode, offDatesByEmployee]);

  // Open multi-date modal
  const openMultiDateModal = (employee, category) => {
    setSelectedEmployee(employee);
    setSelectedCategory(category);
    setSelectedDates([]);
    setMultiDateModalVisible(true);
  };

  // Handle multi-date submission
  const handleMultiDateSubmit = async () => {
    if (selectedDates.length === 0) {
      message.warning('Veuillez sélectionner au moins une date');
      return;
    }

    try {
      const entries = selectedDates.map(date => ({
        employee_id: selectedEmployee.id,
        off_date: dayjs(date).format('YYYY-MM-DD'),
        category: selectedCategory
      }));

      const { error } = await addMultipleOffDates(entries);
      if (error) throw error;

      message.success(`${selectedDates.length} date(s) ${selectedCategory} ajoutée(s) pour ${getDisplayName(selectedEmployee)}`);
      setMultiDateModalVisible(false);
      setSelectedDates([]);
      loadData();
    } catch (error) {
      message.error(`Erreur lors de l'ajout: ${error.message || error}`);
      console.error('Error adding:', error);
    }
  };

  // Open bulk modal for a specific date
  const openBulkModal = () => {
    setBulkSelectedEmployees([]);
    setBulkCategory('OFF');
    setBulkSearchQuery('');
    setBulkDate(selectedDate);
    setBulkModalVisible(true);
  };

  // Filter employees for bulk modal
  const bulkFilteredEmployees = useMemo(() => {
    if (!bulkSearchQuery.trim()) return employees;
    const query = bulkSearchQuery.toLowerCase();
    return employees.filter(emp =>
      emp.name?.toLowerCase().includes(query) ||
      emp.first_name?.toLowerCase().includes(query) ||
      emp.role?.toLowerCase().includes(query)
    );
  }, [employees, bulkSearchQuery]);

  // Toggle employee selection in bulk modal
  const toggleBulkEmployee = (employeeId) => {
    setBulkSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Handle bulk submission
  const handleBulkSubmit = async () => {
    if (bulkSelectedEmployees.length === 0) {
      message.warning('Veuillez sélectionner au moins un employé');
      return;
    }

    try {
      const dateStr = bulkDate.format('YYYY-MM-DD');
      const entries = bulkSelectedEmployees.map(empId => ({
        employee_id: empId,
        off_date: dateStr,
        category: bulkCategory
      }));

      const { error } = await addMultipleOffDates(entries);
      if (error) throw error;

      message.success(`${bulkCategory} ajouté pour ${bulkSelectedEmployees.length} employé(s) le ${bulkDate.format('DD/MM/YYYY')}`);
      setBulkModalVisible(false);
      setBulkSelectedEmployees([]);
      loadData();
    } catch (error) {
      message.error(`Erreur lors de l'ajout: ${error.message || error}`);
      console.error('Error adding:', error);
    }
  };

  // Get category button style
  const getCategoryBtnStyle = (category, isSelected) => {
    const categoryStyles = {
      OFF: { bg: '#f6ffed', border: '#52c41a', color: '#389e0d' },
      ASTREINTE: { bg: '#fff7e6', border: '#fa8c16', color: '#d46b08' },
      BASE: { bg: '#e6fffb', border: '#13c2c2', color: '#08979c' },
      BASE_AM: { bg: '#e6f7ff', border: '#1890ff', color: '#096dd9' },
      BASE_PM: { bg: '#f0f5ff', border: '#597ef7', color: '#2f54eb' }
    };
    const style = categoryStyles[category] || categoryStyles.OFF;
    return {
      padding: `${spacing.sm} ${spacing.lg}`,
      borderRadius: radius.lg,
      border: isSelected ? `3px solid ${style.border}` : `1px solid ${style.border}`,
      background: isSelected ? style.bg : 'transparent',
      color: style.color,
      fontWeight: isSelected ? typography.fontWeight.bold : typography.fontWeight.medium,
      cursor: 'pointer',
      transition: transitions.fast,
      transform: isSelected ? 'scale(1.05)' : 'scale(1)'
    };
  };

  // Quick add OFF or ASTREINTE
  const handleQuickAdd = async (employeeId, category) => {
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      console.log('Adding off date:', { employeeId, dateStr, category });
      const { data, error } = await addMultipleOffDates([{
        employee_id: employeeId,
        off_date: dateStr,
        category: category
      }]);

      console.log('Supabase response:', { data, error });
      if (error) throw error;

      message.success(`${category} ajouté pour le ${selectedDate.format('DD/MM/YYYY')}`);
      loadData();
    } catch (error) {
      message.error(`Erreur lors de l'ajout: ${error.message || error}`);
      console.error('Error adding:', error);
    }
  };

  // Delete entry
  const handleDelete = async (id) => {
    try {
      const { error } = await removeOffDate(id);
      if (error) throw error;
      message.success('Entrée supprimée');
      loadData();
    } catch (error) {
      message.error('Erreur lors de la suppression');
      console.error('Error deleting:', error);
    }
  };

  // Get full display name (first_name + name)
  const getDisplayName = (employee) => {
    if (!employee) return 'Unknown';
    const firstName = employee.first_name || '';
    const lastName = employee.name || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown';
  };

  // Get initials from full name
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // Format relative date
  const formatRelativeDate = (date) => {
    const d = dayjs(date);
    const now = dayjs();
    const diffDays = now.diff(d, 'day');

    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaine(s)`;
    return d.format('DD/MM/YYYY');
  };

  // --- Overview view helpers ---
  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    if (!emp) return 'Unknown';
    const firstName = emp.first_name || '';
    const lastName = emp.name || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown';
  };

  // Filter and sort entries for overview
  const overviewEntries = useMemo(() => {
    let entries = [...offDates];

    if (overviewFilter === 'OFF') {
      entries = entries.filter(e => e.category === 'OFF');
    } else if (overviewFilter === 'ASTREINTE') {
      entries = entries.filter(e => e.category === 'ASTREINTE');
    } else if (overviewFilter === 'BASE') {
      entries = entries.filter(e => e.category === 'BASE' || e.category === 'BASE_AM' || e.category === 'BASE_PM');
    }

    entries.sort((a, b) => dayjs(b.off_date).unix() - dayjs(a.off_date).unix());
    return entries.slice(0, 50);
  }, [offDates, overviewFilter]);

  // Group entries by date for overview
  const groupedByDate = useMemo(() => {
    const groups = {};
    overviewEntries.forEach(entry => {
      const dateKey = entry.off_date;
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(entry);
    });
    return groups;
  }, [overviewEntries]);

  const getOverviewCategoryStyle = (category) => {
    switch (category) {
      case 'OFF':
        return { bg: '#f6ffed', color: '#389e0d', border: '#b7eb8f' };
      case 'ASTREINTE':
        return { bg: '#fff7e6', color: '#d46b08', border: '#ffd591' };
      case 'BASE':
        return { bg: '#e6fffb', color: '#08979c', border: '#87e8de' };
      case 'BASE_AM':
        return { bg: '#e6f7ff', color: '#096dd9', border: '#91d5ff' };
      case 'BASE_PM':
        return { bg: '#f0f5ff', color: '#2f54eb', border: '#adc6ff' };
      default:
        return { bg: '#f5f5f5', color: '#666', border: '#d9d9d9' };
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'OFF': return '🏖️';
      case 'ASTREINTE': return '🚨';
      case 'BASE': return '🏢';
      case 'BASE_AM': return '🌅';
      case 'BASE_PM': return '🌇';
      default: return '📅';
    }
  };

  const formatOverviewDate = (dateStr) => {
    const date = dayjs(dateStr);
    const today = dayjs().startOf('day');
    const diff = date.diff(today, 'day');

    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Demain';
    if (diff === -1) return 'Hier';
    if (diff > 1 && diff <= 7) return `Dans ${diff} jours`;
    if (diff < -1 && diff >= -7) return `Il y a ${Math.abs(diff)} jours`;
    return date.format('ddd D MMM');
  };

  const overviewFilterBtnStyle = (active) => ({
    padding: `${spacing.sm} ${spacing.lg}`,
    borderRadius: radius.lg,
    border: active ? 'none' : `1px solid ${colors.borderMedium}`,
    background: active ? colors.bgDark : colors.bgSecondary,
    color: active ? colors.textInverse : colors.textSecondary,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.sm,
    cursor: 'pointer',
    transition: transitions.fast
  });

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingState}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={{ ...styles.header, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
            <div style={styles.headerIcon}>📅</div>
            <div>
              <h1 style={styles.headerTitle}>Gestion OFF & Astreintes</h1>
              <p style={styles.headerSubtitle}>
                {viewMode === 'manage' ? 'Gérez les jours OFF et Astreintes par employé' : 'Aperçu chronologique des entrées'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
            {/* Export Buttons */}
            <div style={{ display: 'flex', gap: spacing.sm }}>
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleDownloadExcel}
                style={{
                  height: '40px',
                  borderRadius: radius.lg,
                  background: '#217346',
                  border: 'none',
                  color: '#fff',
                  fontWeight: typography.fontWeight.semibold,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs
                }}
                title="Télécharger au format Excel"
              >
                Télécharger Excel
              </Button>
              {sheetUrl && (
                <Button
                  icon={<LinkOutlined />}
                  onClick={() => window.open(sheetUrl, '_blank')}
                  style={{
                    height: '40px',
                    borderRadius: radius.lg,
                    border: `1px solid ${colors.borderMedium}`,
                    fontWeight: typography.fontWeight.medium,
                  }}
                  title="Ouvrir la Google Sheet"
                >
                  Ouvrir Sheet
                </Button>
              )}
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={handleExportToSheets}
                loading={exporting}
                style={{
                  height: '40px',
                  borderRadius: radius.lg,
                  background: '#0f9d58',
                  border: 'none',
                  fontWeight: typography.fontWeight.semibold,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs
                }}
              >
                {exporting ? 'Mise à jour...' : 'Mettre à jour'}
              </Button>
            </div>

            {/* View Toggle */}
            <div style={{
              display: 'flex',
              background: colors.bgTertiary,
              borderRadius: radius.xl,
              padding: '4px',
            }}>
              <button
                onClick={() => setViewMode('manage')}
                style={{
                  padding: `${spacing.sm} ${spacing.lg}`,
                  borderRadius: radius.lg,
                  border: 'none',
                  background: viewMode === 'manage' ? colors.bgDark : 'transparent',
                  color: viewMode === 'manage' ? colors.textInverse : colors.textSecondary,
                  fontWeight: typography.fontWeight.semibold,
                  fontSize: typography.fontSize.sm,
                  cursor: 'pointer',
                  transition: transitions.fast,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                }}
              >
                <AppstoreOutlined /> Gestion
              </button>
              <button
                onClick={() => setViewMode('overview')}
                style={{
                  padding: `${spacing.sm} ${spacing.lg}`,
                  borderRadius: radius.lg,
                  border: 'none',
                  background: viewMode === 'overview' ? colors.bgDark : 'transparent',
                  color: viewMode === 'overview' ? colors.textInverse : colors.textSecondary,
                  fontWeight: typography.fontWeight.semibold,
                  fontSize: typography.fontSize.sm,
                  cursor: 'pointer',
                  transition: transitions.fast,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                }}
              >
                <UnorderedListOutlined /> Aperçu
              </button>
            </div>
          </div>
        </div>

        {/* MANAGE VIEW */}
        {viewMode === 'manage' && (
          <>
        {/* Search & Date Selection */}
        <div style={styles.searchSection}>
          <div style={{ display: 'flex', gap: spacing.lg, alignItems: 'center', flexWrap: 'wrap' }}>
            <Input
              placeholder="Rechercher un employé..."
              prefix={<SearchOutlined style={{ color: colors.textMuted }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ ...styles.searchInput, flex: 1, minWidth: '300px' }}
              allowClear
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
              <Text style={{ color: colors.textTertiary, fontWeight: typography.fontWeight.medium }}>
                Date:
              </Text>
              <DatePicker
                value={selectedDate}
                onChange={(date) => setSelectedDate(date || dayjs())}
                format="DD/MM/YYYY"
                style={{
                  height: '48px',
                  borderRadius: radius.lg,
                  border: `2px solid ${colors.borderLight}`,
                }}
              />
              <Button
                type="primary"
                onClick={openBulkModal}
                style={{
                  height: '48px',
                  borderRadius: radius.lg,
                  background: colors.bgDark,
                  border: 'none',
                  fontWeight: typography.fontWeight.semibold,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm
                }}
              >
                <PlusOutlined /> Ajout Multiple
              </Button>
            </div>
          </div>

          {/* Sort Options */}
          <div style={{ marginTop: spacing.lg }}>
            <Text style={{ color: colors.textTertiary, fontWeight: typography.fontWeight.medium, display: 'flex', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.md }}>
              <SortAscendingOutlined /> Trier par :
            </Text>

            {/* OFF Sort */}
            <div style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.sm, flexWrap: 'wrap' }}>
              <Text style={{ color: colors.textMuted, fontSize: typography.fontSize.sm, minWidth: '60px', display: 'flex', alignItems: 'center' }}>🏖️ OFF:</Text>
              <button
                onClick={() => setSortMode(sortMode === 'off_old' ? 'none' : 'off_old')}
                style={{
                  padding: `${spacing.xs} ${spacing.md}`,
                  borderRadius: radius.md,
                  border: sortMode === 'off_old' ? '2px solid #52c41a' : `1px solid ${colors.borderMedium}`,
                  background: sortMode === 'off_old' ? '#f6ffed' : colors.bgSecondary,
                  color: sortMode === 'off_old' ? '#389e0d' : colors.textSecondary,
                  fontWeight: sortMode === 'off_old' ? typography.fontWeight.bold : typography.fontWeight.medium,
                  fontSize: typography.fontSize.xs,
                  cursor: 'pointer',
                  transition: transitions.fast,
                }}
              >
                ⬆️ Plus ancien
              </button>
              <button
                onClick={() => setSortMode(sortMode === 'off_recent' ? 'none' : 'off_recent')}
                style={{
                  padding: `${spacing.xs} ${spacing.md}`,
                  borderRadius: radius.md,
                  border: sortMode === 'off_recent' ? '2px solid #52c41a' : `1px solid ${colors.borderMedium}`,
                  background: sortMode === 'off_recent' ? '#f6ffed' : colors.bgSecondary,
                  color: sortMode === 'off_recent' ? '#389e0d' : colors.textSecondary,
                  fontWeight: sortMode === 'off_recent' ? typography.fontWeight.bold : typography.fontWeight.medium,
                  fontSize: typography.fontSize.xs,
                  cursor: 'pointer',
                  transition: transitions.fast,
                }}
              >
                ⬇️ Plus récent
              </button>
            </div>

            {/* Astreinte Sort */}
            <div style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.sm, flexWrap: 'wrap' }}>
              <Text style={{ color: colors.textMuted, fontSize: typography.fontSize.sm, minWidth: '60px', display: 'flex', alignItems: 'center' }}>🚨 Astreinte:</Text>
              <button
                onClick={() => setSortMode(sortMode === 'astreinte_old' ? 'none' : 'astreinte_old')}
                style={{
                  padding: `${spacing.xs} ${spacing.md}`,
                  borderRadius: radius.md,
                  border: sortMode === 'astreinte_old' ? '2px solid #fa8c16' : `1px solid ${colors.borderMedium}`,
                  background: sortMode === 'astreinte_old' ? '#fff7e6' : colors.bgSecondary,
                  color: sortMode === 'astreinte_old' ? '#d46b08' : colors.textSecondary,
                  fontWeight: sortMode === 'astreinte_old' ? typography.fontWeight.bold : typography.fontWeight.medium,
                  fontSize: typography.fontSize.xs,
                  cursor: 'pointer',
                  transition: transitions.fast,
                }}
              >
                ⬆️ Plus ancien
              </button>
              <button
                onClick={() => setSortMode(sortMode === 'astreinte_recent' ? 'none' : 'astreinte_recent')}
                style={{
                  padding: `${spacing.xs} ${spacing.md}`,
                  borderRadius: radius.md,
                  border: sortMode === 'astreinte_recent' ? '2px solid #fa8c16' : `1px solid ${colors.borderMedium}`,
                  background: sortMode === 'astreinte_recent' ? '#fff7e6' : colors.bgSecondary,
                  color: sortMode === 'astreinte_recent' ? '#d46b08' : colors.textSecondary,
                  fontWeight: sortMode === 'astreinte_recent' ? typography.fontWeight.bold : typography.fontWeight.medium,
                  fontSize: typography.fontSize.xs,
                  cursor: 'pointer',
                  transition: transitions.fast,
                }}
              >
                ⬇️ Plus récent
              </button>
            </div>

            {/* Base Sort */}
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
              <Text style={{ color: colors.textMuted, fontSize: typography.fontSize.sm, minWidth: '60px', display: 'flex', alignItems: 'center' }}>🏢 Base:</Text>
              <button
                onClick={() => setSortMode(sortMode === 'base_old' ? 'none' : 'base_old')}
                style={{
                  padding: `${spacing.xs} ${spacing.md}`,
                  borderRadius: radius.md,
                  border: sortMode === 'base_old' ? '2px solid #1890ff' : `1px solid ${colors.borderMedium}`,
                  background: sortMode === 'base_old' ? '#e6f7ff' : colors.bgSecondary,
                  color: sortMode === 'base_old' ? '#096dd9' : colors.textSecondary,
                  fontWeight: sortMode === 'base_old' ? typography.fontWeight.bold : typography.fontWeight.medium,
                  fontSize: typography.fontSize.xs,
                  cursor: 'pointer',
                  transition: transitions.fast,
                }}
              >
                ⬆️ Plus ancien
              </button>
              <button
                onClick={() => setSortMode(sortMode === 'base_recent' ? 'none' : 'base_recent')}
                style={{
                  padding: `${spacing.xs} ${spacing.md}`,
                  borderRadius: radius.md,
                  border: sortMode === 'base_recent' ? '2px solid #1890ff' : `1px solid ${colors.borderMedium}`,
                  background: sortMode === 'base_recent' ? '#e6f7ff' : colors.bgSecondary,
                  color: sortMode === 'base_recent' ? '#096dd9' : colors.textSecondary,
                  fontWeight: sortMode === 'base_recent' ? typography.fontWeight.bold : typography.fontWeight.medium,
                  fontSize: typography.fontSize.xs,
                  cursor: 'pointer',
                  transition: transitions.fast,
                }}
              >
                ⬇️ Plus récent
              </button>
            </div>

            {/* Reset button when sorted */}
            {sortMode !== 'none' && (
              <Button
                type="link"
                onClick={() => setSortMode('none')}
                style={{ marginTop: spacing.sm, padding: 0, color: colors.textTertiary }}
              >
                ✕ Annuler le tri
              </Button>
            )}
          </div>
        </div>

        {/* Recommendations Section for Planning */}
        {showRecommendations && employees.length > 0 && (
          <div style={{
            marginBottom: spacing.xxxl,
            background: `linear-gradient(135deg, ${colors.bgSecondary} 0%, ${colors.bgTertiary} 100%)`,
            borderRadius: radius.xxxl,
            padding: spacing.xl,
            border: `2px solid ${colors.borderLight}`,
            boxShadow: shadows.lg
          }}>
            {/* Section Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: spacing.xl,
              paddingBottom: spacing.lg,
              borderBottom: `1px solid ${colors.borderLight}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  background: colors.bgDark,
                  borderRadius: radius.xl,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px'
                }}>
                  📋
                </div>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: typography.fontSize.lg,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary
                  }}>
                    Recommandations pour le Planning
                  </h2>
                  <p style={{
                    margin: 0,
                    fontSize: typography.fontSize.sm,
                    color: colors.textTertiary
                  }}>
                    Employés prioritaires pour OFF et Astreinte
                  </p>
                </div>
              </div>
              <Button
                type="text"
                onClick={() => setShowRecommendations(false)}
                style={{ color: colors.textMuted }}
              >
                ✕ Masquer
              </Button>
            </div>

            {/* Two columns: Need OFF / Need Astreinte */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: spacing.xl
            }}>
              {/* Need OFF Column */}
              <div style={{
                background: '#f6ffed',
                borderRadius: radius.xxl,
                padding: spacing.lg,
                border: '2px solid #b7eb8f'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  marginBottom: spacing.lg,
                  paddingBottom: spacing.md,
                  borderBottom: '1px solid #b7eb8f'
                }}>
                  <span style={{ fontSize: '24px' }}>🏖️</span>
                  <span style={{
                    fontSize: typography.fontSize.md,
                    fontWeight: typography.fontWeight.bold,
                    color: '#389e0d'
                  }}>
                    Besoin d'un OFF
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                  {recommendations.needOff.map((item, index) => (
                    <div
                      key={item.employee.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: spacing.md,
                        background: index === 0 ? '#d9f7be' : 'rgba(255,255,255,0.7)',
                        borderRadius: radius.lg,
                        border: index === 0 ? '2px solid #52c41a' : '1px solid #b7eb8f'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          background: index === 0 ? '#52c41a' : '#95de64',
                          borderRadius: radius.md,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: typography.fontWeight.bold,
                          fontSize: typography.fontSize.sm
                        }}>
                          {index + 1}
                        </div>
                        <div>
                          <div style={{
                            fontSize: typography.fontSize.sm,
                            fontWeight: index === 0 ? typography.fontWeight.bold : typography.fontWeight.medium,
                            color: '#1d3712'
                          }}>
                            {getDisplayName(item.employee)}
                          </div>
                          <div style={{
                            fontSize: typography.fontSize.xs,
                            color: '#389e0d'
                          }}>
                            {item.employee.role || 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div style={{
                        textAlign: 'right'
                      }}>
                        {item.hasNeverHadOff ? (
                          <Tag color="red" style={{ margin: 0, borderRadius: radius.sm }}>
                            Jamais eu
                          </Tag>
                        ) : (
                          <div>
                            <div style={{
                              fontSize: typography.fontSize.lg,
                              fontWeight: typography.fontWeight.bold,
                              color: item.daysSinceOff > 30 ? '#cf1322' : '#389e0d'
                            }}>
                              {item.daysSinceOff}j
                            </div>
                            <div style={{
                              fontSize: typography.fontSize.xs,
                              color: '#52c41a'
                            }}>
                              depuis dernier OFF
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Need Astreinte Column */}
              <div style={{
                background: '#fff7e6',
                borderRadius: radius.xxl,
                padding: spacing.lg,
                border: '2px solid #ffd591'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  marginBottom: spacing.lg,
                  paddingBottom: spacing.md,
                  borderBottom: '1px solid #ffd591'
                }}>
                  <span style={{ fontSize: '24px' }}>🚨</span>
                  <span style={{
                    fontSize: typography.fontSize.md,
                    fontWeight: typography.fontWeight.bold,
                    color: '#d46b08'
                  }}>
                    Besoin d'une Astreinte
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                  {recommendations.needAstreinte.map((item, index) => (
                    <div
                      key={item.employee.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: spacing.md,
                        background: index === 0 ? '#ffe7ba' : 'rgba(255,255,255,0.7)',
                        borderRadius: radius.lg,
                        border: index === 0 ? '2px solid #fa8c16' : '1px solid #ffd591'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          background: index === 0 ? '#fa8c16' : '#ffc069',
                          borderRadius: radius.md,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: typography.fontWeight.bold,
                          fontSize: typography.fontSize.sm
                        }}>
                          {index + 1}
                        </div>
                        <div>
                          <div style={{
                            fontSize: typography.fontSize.sm,
                            fontWeight: index === 0 ? typography.fontWeight.bold : typography.fontWeight.medium,
                            color: '#613400'
                          }}>
                            {getDisplayName(item.employee)}
                          </div>
                          <div style={{
                            fontSize: typography.fontSize.xs,
                            color: '#d46b08'
                          }}>
                            {item.employee.role || 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div style={{
                        textAlign: 'right'
                      }}>
                        {item.hasNeverHadAstreinte ? (
                          <Tag color="red" style={{ margin: 0, borderRadius: radius.sm }}>
                            Jamais eu
                          </Tag>
                        ) : (
                          <div>
                            <div style={{
                              fontSize: typography.fontSize.lg,
                              fontWeight: typography.fontWeight.bold,
                              color: item.daysSinceAstreinte > 30 ? '#cf1322' : '#d46b08'
                            }}>
                              {item.daysSinceAstreinte}j
                            </div>
                            <div style={{
                              fontSize: typography.fontSize.xs,
                              color: '#fa8c16'
                            }}>
                              depuis dernière Astreinte
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick action hint */}
            <div style={{
              marginTop: spacing.lg,
              padding: spacing.md,
              background: colors.bgOverlay,
              borderRadius: radius.lg,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm
            }}>
              <span style={{ fontSize: '16px' }}>💡</span>
              <Text style={{ fontSize: typography.fontSize.sm, color: colors.textTertiary }}>
                Sélectionnez une date ci-dessus puis cliquez sur les boutons <strong>OFF</strong> ou <strong>Astreinte</strong> dans la carte de l'employé pour ajouter rapidement.
              </Text>
            </div>
          </div>
        )}

        {/* Show recommendations toggle if hidden */}
        {!showRecommendations && (
          <Button
            type="dashed"
            onClick={() => setShowRecommendations(true)}
            style={{
              marginBottom: spacing.xl,
              width: '100%',
              height: '44px',
              borderRadius: radius.lg,
              color: colors.textTertiary
            }}
          >
            📋 Afficher les recommandations pour le planning
          </Button>
        )}

        {/* Employee Cards */}
        {filteredEmployees.length === 0 ? (
          <div style={styles.emptyState}>
            <Empty
              description={
                <span style={{ color: colors.textTertiary }}>
                  {searchQuery ? 'Aucun employé trouvé' : 'Aucun employé'}
                </span>
              }
            />
          </div>
        ) : (
          <div style={styles.employeeGrid}>
            {filteredEmployees.map(employee => {
              const stats = getEmployeeStats(employee.id);
              return (
                <div key={employee.id} style={styles.employeeCard}>
                  {/* Employee Header */}
                  <div style={styles.employeeHeader}>
                    <div style={styles.employeeInfo}>
                      <div style={styles.employeeAvatar}>
                        {getInitials(getDisplayName(employee))}
                      </div>
                      <div>
                        <h3 style={styles.employeeName}>{getDisplayName(employee)}</h3>
                        <p style={styles.employeeRole}>{employee.role || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Badges */}
                  <div style={styles.statsRow}>
                    <span style={{
                      ...styles.statBadge,
                      background: '#f6ffed',
                      color: '#389e0d',
                      border: '1px solid #b7eb8f'
                    }}>
                      🏖️ {stats.offCount} OFF
                    </span>
                    <span style={{
                      ...styles.statBadge,
                      background: '#fff7e6',
                      color: '#d46b08',
                      border: '1px solid #ffd591'
                    }}>
                      🚨 {stats.astreinteCount} Astreintes
                    </span>
                    <span style={{
                      ...styles.statBadge,
                      background: '#e6f7ff',
                      color: '#096dd9',
                      border: '1px solid #91d5ff'
                    }}>
                      🏢 {stats.baseAmCount + stats.basePmCount + stats.baseCount} Base
                    </span>
                  </div>

                  {/* Last OFF/Astreinte/Base Dates */}
                  <div style={styles.lastDateSection}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: spacing.md }}>
                      <div>
                        <div style={styles.lastDateLabel}>
                          <ClockCircleOutlined /> Dernier OFF
                        </div>
                        {stats.lastOff ? (
                          <div>
                            <span style={styles.lastDateValue}>
                              {dayjs(stats.lastOff.off_date).format('DD/MM')}
                            </span>
                            <span style={styles.lastDateAgo}>
                              ({formatRelativeDate(stats.lastOff.off_date)})
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
                            Aucun
                          </span>
                        )}
                      </div>
                      <div>
                        <div style={styles.lastDateLabel}>
                          <ClockCircleOutlined /> Dernière Astreinte
                        </div>
                        {stats.lastAstreinte ? (
                          <div>
                            <span style={styles.lastDateValue}>
                              {dayjs(stats.lastAstreinte.off_date).format('DD/MM')}
                            </span>
                            <span style={styles.lastDateAgo}>
                              ({formatRelativeDate(stats.lastAstreinte.off_date)})
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
                            Aucune
                          </span>
                        )}
                      </div>
                      <div>
                        <div style={styles.lastDateLabel}>
                          <ClockCircleOutlined /> Dernière Base
                        </div>
                        {stats.lastBase ? (
                          <div>
                            <span style={styles.lastDateValue}>
                              {dayjs(stats.lastBase.off_date).format('DD/MM')}
                            </span>
                            <span style={styles.lastDateAgo}>
                              ({stats.lastBase.category.replace('_', ' ')})
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
                            Aucune
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Add Buttons */}
                  <div style={styles.quickAddSection}>
                    <Button
                      style={{ ...styles.quickAddBtn, ...styles.offBtn }}
                      onClick={() => handleQuickAdd(employee.id, 'OFF')}
                    >
                      <PlusOutlined /> OFF
                    </Button>
                    <Button
                      style={{ ...styles.quickAddBtn, ...styles.astreinteBtn }}
                      onClick={() => handleQuickAdd(employee.id, 'ASTREINTE')}
                    >
                      <PlusOutlined /> Astreinte
                    </Button>
                    <Button
                      style={{ ...styles.quickAddBtn, ...styles.baseBtn }}
                      onClick={() => handleQuickAdd(employee.id, 'BASE')}
                    >
                      <PlusOutlined /> Base
                    </Button>
                    <Button
                      style={{ ...styles.quickAddBtn, ...styles.baseAmBtn }}
                      onClick={() => handleQuickAdd(employee.id, 'BASE_AM')}
                    >
                      <PlusOutlined /> Base AM
                    </Button>
                    <Button
                      style={{ ...styles.quickAddBtn, ...styles.basePmBtn }}
                      onClick={() => handleQuickAdd(employee.id, 'BASE_PM')}
                    >
                      <PlusOutlined /> Base PM
                    </Button>
                  </div>
                  <Text style={{ fontSize: typography.fontSize.xs, color: colors.textMuted, marginTop: spacing.sm, display: 'block' }}>
                    pour le {selectedDate.format('DD/MM/YYYY')}
                  </Text>

                  {/* Recent History */}
                  {stats.recentDates.length > 0 && (
                    <div style={styles.historySection}>
                      <div style={styles.historyLabel}>Historique récent</div>
                      <div style={styles.historyList}>
                        {stats.recentDates.map(entry => (
                          <div key={entry.id} style={styles.historyItem}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                              <Tag
                                color={
                                  entry.category === 'OFF' ? 'green' :
                                  entry.category === 'ASTREINTE' ? 'orange' :
                                  entry.category === 'BASE_AM' ? 'blue' :
                                  entry.category === 'BASE_PM' ? 'geekblue' :
                                  entry.category === 'BASE' ? 'cyan' : 'default'
                                }
                                style={{ margin: 0, borderRadius: radius.sm }}
                              >
                                {entry.category === 'OFF' ? '🏖️' :
                                 entry.category === 'ASTREINTE' ? '🚨' :
                                 entry.category === 'BASE_AM' ? '🌅' :
                                 entry.category === 'BASE_PM' ? '🌇' :
                                 entry.category === 'BASE' ? '🏢' : '🌊'} {entry.category.replace('_', ' ')}
                              </Tag>
                              <span style={{ color: colors.textSecondary }}>
                                {dayjs(entry.off_date).format('DD/MM/YYYY')}
                              </span>
                            </div>
                            <Popconfirm
                              title="Supprimer cette entrée?"
                              onConfirm={() => handleDelete(entry.id)}
                              okText="Oui"
                              cancelText="Non"
                            >
                              <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                style={{ opacity: 0.6 }}
                              />
                            </Popconfirm>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
          </>
        )}

        {/* OVERVIEW VIEW */}
        {viewMode === 'overview' && (
          <>
            {/* Quick Stats */}
            <div style={{
              display: 'flex',
              gap: spacing.lg,
              marginBottom: spacing.xl
            }}>
              <div style={{
                padding: `${spacing.md} ${spacing.lg}`,
                background: '#f6ffed',
                borderRadius: radius.xl,
                border: '1px solid #b7eb8f',
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm
              }}>
                <span style={{ fontSize: '24px' }}>🏖️</span>
                <div>
                  <span style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: '#389e0d' }}>
                    {offDates.filter(e => e.category === 'OFF').length}
                  </span>
                  <span style={{ fontSize: typography.fontSize.sm, color: '#389e0d', marginLeft: spacing.xs }}>OFF</span>
                </div>
              </div>
              <div style={{
                padding: `${spacing.md} ${spacing.lg}`,
                background: '#fff7e6',
                borderRadius: radius.xl,
                border: '1px solid #ffd591',
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm
              }}>
                <span style={{ fontSize: '24px' }}>🚨</span>
                <div>
                  <span style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: '#d46b08' }}>
                    {offDates.filter(e => e.category === 'ASTREINTE').length}
                  </span>
                  <span style={{ fontSize: typography.fontSize.sm, color: '#d46b08', marginLeft: spacing.xs }}>Astreintes</span>
                </div>
              </div>
              <div style={{
                padding: `${spacing.md} ${spacing.lg}`,
                background: '#e6f7ff',
                borderRadius: radius.xl,
                border: '1px solid #91d5ff',
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm
              }}>
                <span style={{ fontSize: '24px' }}>🏢</span>
                <div>
                  <span style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: '#096dd9' }}>
                    {offDates.filter(e => e.category === 'BASE' || e.category === 'BASE_AM' || e.category === 'BASE_PM').length}
                  </span>
                  <span style={{ fontSize: typography.fontSize.sm, color: '#096dd9', marginLeft: spacing.xs }}>Base</span>
                </div>
              </div>
            </div>

            {/* Filter Buttons */}
            <div style={{
              display: 'flex',
              gap: spacing.sm,
              marginBottom: spacing.xl
            }}>
              <button onClick={() => setOverviewFilter('all')} style={overviewFilterBtnStyle(overviewFilter === 'all')}>
                Tout
              </button>
              <button onClick={() => setOverviewFilter('OFF')} style={overviewFilterBtnStyle(overviewFilter === 'OFF')}>
                🏖️ OFF
              </button>
              <button onClick={() => setOverviewFilter('ASTREINTE')} style={overviewFilterBtnStyle(overviewFilter === 'ASTREINTE')}>
                🚨 Astreintes
              </button>
              <button onClick={() => setOverviewFilter('BASE')} style={overviewFilterBtnStyle(overviewFilter === 'BASE')}>
                🏢 Base
              </button>
            </div>

            {/* Entries List by Date */}
            {Object.keys(groupedByDate).length === 0 ? (
              <Empty description="Aucune entrée" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
                {Object.entries(groupedByDate).map(([date, entries]) => (
                  <div key={date} style={{
                    background: colors.bgSecondary,
                    borderRadius: radius.xxxl,
                    padding: spacing.xl,
                    border: `1px solid ${colors.borderLight}`
                  }}>
                    {/* Date Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: spacing.lg,
                      paddingBottom: spacing.md,
                      borderBottom: `1px solid ${colors.borderLight}`
                    }}>
                      <div style={{
                        fontSize: typography.fontSize.lg,
                        fontWeight: typography.fontWeight.bold,
                        color: colors.textPrimary,
                        textTransform: 'capitalize'
                      }}>
                        {dayjs(date).format('dddd D MMMM YYYY')}
                      </div>
                      <div style={{
                        fontSize: typography.fontSize.sm,
                        color: colors.textTertiary,
                        background: colors.bgTertiary,
                        padding: `${spacing.xs} ${spacing.md}`,
                        borderRadius: radius.lg
                      }}>
                        {formatOverviewDate(date)}
                      </div>
                    </div>

                    {/* Entries for this date */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: spacing.md
                    }}>
                      {entries.map(entry => {
                        const style = getOverviewCategoryStyle(entry.category);
                        return (
                          <div key={entry.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.md,
                            padding: spacing.md,
                            background: style.bg,
                            borderRadius: radius.xl,
                            border: `1px solid ${style.border}`
                          }}>
                            <span style={{ fontSize: '20px' }}>{getCategoryIcon(entry.category)}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontSize: typography.fontSize.md,
                                fontWeight: typography.fontWeight.semibold,
                                color: style.color,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {getEmployeeName(entry.employee_id)}
                              </div>
                              <div style={{
                                fontSize: typography.fontSize.xs,
                                color: style.color,
                                opacity: 0.8
                              }}>
                                {entry.category.replace('_', ' ')}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Multi-Date Selection Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
              <CalendarFilled style={{ fontSize: '24px', color: colors.bgDark }} />
              <div>
                <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold }}>
                  Sélection Multiple - {selectedCategory}
                </div>
                {selectedEmployee && (
                  <div style={{ fontSize: typography.fontSize.sm, color: colors.textTertiary, fontWeight: typography.fontWeight.normal }}>
                    Pour {getDisplayName(selectedEmployee)}
                  </div>
                )}
              </div>
            </div>
          }
          open={multiDateModalVisible}
          onCancel={() => {
            setMultiDateModalVisible(false);
            setSelectedDates([]);
          }}
          onOk={handleMultiDateSubmit}
          okText={`Ajouter ${selectedDates.length} date(s)`}
          cancelText="Annuler"
          width={700}
          okButtonProps={{
            disabled: selectedDates.length === 0,
            style: {
              background: selectedDates.length > 0 ? colors.bgDark : colors.bgTertiary,
              borderColor: selectedDates.length > 0 ? colors.bgDark : colors.borderLight
            }
          }}
        >
          <div style={{ padding: `${spacing.lg} 0` }}>
            <Text style={{
              display: 'block',
              marginBottom: spacing.lg,
              fontSize: typography.fontSize.md,
              color: colors.textSecondary
            }}>
              Cliquez sur les dates dans le calendrier ci-dessous pour sélectionner plusieurs jours {selectedCategory}.
            </Text>

            <DatePicker
              multiple
              value={selectedDates}
              onChange={(dates) => setSelectedDates(dates || [])}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Sélectionnez plusieurs dates..."
              size="large"
            />

            {selectedDates.length > 0 && (
              <div style={{
                marginTop: spacing.lg,
                padding: spacing.lg,
                background: colors.bgSecondary,
                borderRadius: radius.lg,
                border: `1px solid ${colors.borderLight}`
              }}>
                <Text style={{
                  display: 'block',
                  marginBottom: spacing.sm,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.textSecondary
                }}>
                  Dates sélectionnées ({selectedDates.length}):
                </Text>
                <div style={{
                  display: 'flex',
                  gap: spacing.xs,
                  flexWrap: 'wrap'
                }}>
                  {selectedDates.map((date, index) => (
                    <Tag
                      key={index}
                      closable
                      onClose={() => {
                        setSelectedDates(selectedDates.filter((_, i) => i !== index));
                      }}
                      color={
                        selectedCategory === 'OFF' ? 'green' :
                        selectedCategory === 'ASTREINTE' ? 'orange' :
                        selectedCategory === 'BASE_AM' ? 'blue' :
                        selectedCategory === 'BASE_PM' ? 'geekblue' :
                        selectedCategory === 'BASE' ? 'cyan' : 'default'
                      }
                      style={{
                        fontSize: typography.fontSize.sm,
                        padding: `${spacing.xs} ${spacing.sm}`,
                        borderRadius: radius.md
                      }}
                    >
                      {dayjs(date).format('DD/MM/YYYY')}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>

        {/* Bulk Employee Selection Modal */}
        <Modal
          title={null}
          open={bulkModalVisible}
          onCancel={() => {
            setBulkModalVisible(false);
            setBulkSelectedEmployees([]);
          }}
          footer={null}
          width={600}
          styles={{
            content: { borderRadius: radius.xxxl, overflow: 'hidden', padding: 0 },
            body: { padding: 0 }
          }}
        >
          {/* Modal Header */}
          <div style={{
            background: colors.bgDark,
            padding: spacing.xl,
            color: colors.textInverse
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: radius.lg,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                <CalendarFilled style={{ color: colors.textInverse }} />
              </div>
              <div>
                <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold }}>
                  Ajout Multiple
                </div>
                <div style={{ fontSize: typography.fontSize.sm, opacity: 0.8 }}>
                  Sélectionnez la date, les employés et la catégorie
                </div>
              </div>
            </div>
          </div>

          {/* Modal Body */}
          <div style={{ padding: spacing.xl }}>
            {/* Date Selection */}
            <div style={{ marginBottom: spacing.xl }}>
              <Text style={{
                display: 'block',
                marginBottom: spacing.md,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wide
              }}>
                Date
              </Text>
              <DatePicker
                value={bulkDate}
                onChange={(date) => setBulkDate(date || dayjs())}
                format="DD/MM/YYYY"
                style={{
                  width: '100%',
                  height: '44px',
                  borderRadius: radius.lg,
                  border: `2px solid ${colors.borderMedium}`,
                }}
                size="large"
              />
            </div>

            {/* Category Selection */}
            <div style={{ marginBottom: spacing.xl }}>
              <Text style={{
                display: 'block',
                marginBottom: spacing.md,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wide
              }}>
                Catégorie
              </Text>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                {['OFF', 'ASTREINTE', 'BASE', 'BASE_AM', 'BASE_PM'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setBulkCategory(cat)}
                    style={getCategoryBtnStyle(cat, bulkCategory === cat)}
                  >
                    {cat === 'OFF' && '🏖️ '}
                    {cat === 'ASTREINTE' && '🚨 '}
                    {cat === 'BASE' && '🏢 '}
                    {cat === 'BASE_AM' && '🌅 '}
                    {cat === 'BASE_PM' && '🌇 '}
                    {cat.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div style={{ marginBottom: spacing.lg }}>
              <Input
                placeholder="Rechercher un employé..."
                prefix={<SearchOutlined style={{ color: colors.textMuted }} />}
                value={bulkSearchQuery}
                onChange={(e) => setBulkSearchQuery(e.target.value)}
                style={{
                  height: '44px',
                  borderRadius: radius.lg,
                  border: `1px solid ${colors.borderLight}`
                }}
                allowClear
              />
            </div>

            {/* Employee List */}
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: `1px solid ${colors.borderLight}`,
              borderRadius: radius.lg
            }}>
              {bulkFilteredEmployees.map((emp, index) => {
                const isSelected = bulkSelectedEmployees.includes(emp.id);
                const displayName = `${emp.first_name || ''} ${emp.name || ''}`.trim() || 'Unknown';
                return (
                  <div
                    key={emp.id}
                    onClick={() => toggleBulkEmployee(emp.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.md,
                      padding: spacing.md,
                      cursor: 'pointer',
                      background: isSelected ? colors.bgTertiary : 'transparent',
                      borderBottom: index < bulkFilteredEmployees.length - 1 ? `1px solid ${colors.borderSubtle}` : 'none',
                      transition: transitions.fast
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: radius.sm,
                      border: isSelected ? `2px solid ${colors.bgDark}` : `2px solid ${colors.borderMedium}`,
                      background: isSelected ? colors.bgDark : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: colors.textInverse,
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      {isSelected && '✓'}
                    </div>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      background: isSelected ? colors.bgDark : colors.bgTertiary,
                      borderRadius: radius.md,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isSelected ? colors.textInverse : colors.textSecondary,
                      fontWeight: typography.fontWeight.bold,
                      fontSize: typography.fontSize.sm
                    }}>
                      {getInitials(displayName)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: typography.fontSize.md,
                        fontWeight: isSelected ? typography.fontWeight.bold : typography.fontWeight.medium,
                        color: colors.textPrimary
                      }}>
                        {displayName}
                      </div>
                      <div style={{
                        fontSize: typography.fontSize.xs,
                        color: colors.textTertiary
                      }}>
                        {emp.role || 'N/A'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Selected Count */}
            {bulkSelectedEmployees.length > 0 && (
              <div style={{
                marginTop: spacing.lg,
                padding: spacing.md,
                background: colors.bgSecondary,
                borderRadius: radius.lg,
                border: `1px solid ${colors.borderLight}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Text style={{ color: colors.textSecondary }}>
                  <strong>{bulkSelectedEmployees.length}</strong> employé(s) sélectionné(s)
                </Text>
                <Button
                  type="link"
                  onClick={() => setBulkSelectedEmployees([])}
                  style={{ color: colors.textTertiary, padding: 0 }}
                >
                  Tout désélectionner
                </Button>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              marginTop: spacing.xl,
              display: 'flex',
              gap: spacing.md
            }}>
              <Button
                onClick={() => {
                  setBulkModalVisible(false);
                  setBulkSelectedEmployees([]);
                }}
                style={{
                  flex: 1,
                  height: '48px',
                  borderRadius: radius.lg,
                  border: `1px solid ${colors.borderMedium}`,
                  fontWeight: typography.fontWeight.medium
                }}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                onClick={handleBulkSubmit}
                disabled={bulkSelectedEmployees.length === 0}
                style={{
                  flex: 2,
                  height: '48px',
                  borderRadius: radius.lg,
                  background: bulkSelectedEmployees.length > 0 ? colors.bgDark : colors.bgTertiary,
                  border: 'none',
                  fontWeight: typography.fontWeight.semibold
                }}
              >
                Ajouter {bulkCategory} le {bulkDate.format('DD/MM')} ({bulkSelectedEmployees.length})
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default OffDays;
